import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from '../contexts/LocationContext'

/**
 * InsightsPanel Component
 *
 * Displays AI-generated insights for the current district and category.
 * Shows summary, key findings, and anomalies with expandable/collapsible UI.
 */
function InsightsPanel({ category }) {
  const { selectedDistrict } = useLocation()
  const [insights, setInsights] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isExpanded, setIsExpanded] = useState(true)

  useEffect(() => {
    async function loadInsights() {
      setLoading(true)
      setError(null)

      try {
        const response = await fetch('/data/insights/insights.json')
        if (!response.ok) {
          throw new Error('Failed to load insights')
        }

        const data = await response.json()
        setInsights(data)
      } catch (err) {
        // Silently handle error - show UI message
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadInsights()
  }, [])

  // Get insights for current district and category
  const getCurrentInsights = () => {
    if (!insights || !selectedDistrict || !category) return null

    const districtKey = selectedDistrict.toLowerCase().replace('d-', '')
    const districtInsights = insights[districtKey]

    if (!districtInsights || !districtInsights[category]) return null

    return districtInsights[category]
  }

  const currentInsights = getCurrentInsights()

  // Don't render if no district selected or not in multi-district mode
  if (!selectedDistrict) {
    return null
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-yellow-600 mt-0.5 mr-3"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm text-yellow-800">
            Unable to load insights. Please try again later.
          </p>
        </div>
      </div>
    )
  }

  // No insights available
  if (!currentInsights) {
    return null
  }

  const {
    summary,
    keyFindings = [],
    anomalies = [],
    generatedAt,
  } = currentInsights

  const getSeverityColor = severity => {
    switch (severity?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatDate = isoDate => {
    if (!isoDate) return ''
    const date = new Date(isoDate)
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }
  const getFinding = finding => {
    if (typeof finding === 'string') return finding
    if (finding && finding.finding) return finding.finding
    if (finding && finding.issue) return finding.issue
    return ''
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsExpanded(!isExpanded)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center space-x-3">
          <div className="bg-blue-600 rounded-lg p-2">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              AI-Generated Insights
            </h3>
            {generatedAt && (
              <p className="text-xs text-gray-500">
                Last updated: {formatDate(generatedAt)}
              </p>
            )}
          </div>
        </div>
        <button
          className="p-1 hover:bg-white/50 rounded-full transition-colors"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <svg
            className={`w-5 h-5 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-6 space-y-6">
          {/* Summary */}
          {summary && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Summary
              </h4>
              <p className="text-gray-800 leading-relaxed">{summary}</p>
            </div>
          )}

          {/* Key Findings */}
          {keyFindings.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Key Findings
              </h4>
              <ul className="space-y-2">
                {keyFindings.map((finding, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-gray-700 leading-relaxed">
                      {getFinding(finding)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Anomalies */}
          {anomalies.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Critical Issues
              </h4>
              <div className="space-y-3">
                {anomalies.map((anomaly, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${getSeverityColor(anomaly.severity)}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-medium text-sm capitalize">
                        {anomaly.type || 'Issue'}
                      </span>
                      <span className="text-xs font-semibold uppercase px-2 py-1 rounded">
                        {anomaly.severity || 'Unknown'}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed">
                      {anomaly.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

InsightsPanel.propTypes = {
  category: PropTypes.string.isRequired,
}

export default InsightsPanel
